/**
 * ConversationTabs - Campaign chat info header (shown when in campaign mode)
 *
 * Shows campaign-specific details:
 * - "Helping with: [Campaign Title]"
 *
 * NOTE: Mode switching is now handled by CopilotModeTabs component.
 * NOTE: Only ONE conversation per campaign is allowed.
 */

import React from 'react';
import {
  Box,
  Flex,
  Text,
  Badge,
} from '@chakra-ui/react';
import { ConversationTabInfo } from 'src/services/campaign-copilot';

interface ConversationTabsProps {
  /** List of conversation tabs (only 1 for campaign mode) */
  conversations: ConversationTabInfo[];
  /** Currently active conversation index */
  activeIndex: number;
  /** Whether the copilot is in campaign mode */
  isCampaignMode: boolean;
  /** Campaign title for display */
  campaignTitle?: string;
  /** Callback when a tab is clicked */
  onSwitchTab: (index: number) => void;
  /** Callback when "+ New Chat" is clicked - not used in single chat mode */
  onCreateTab: () => Promise<void>;
  /** Callback when a tab is deleted - not used in single chat mode */
  onDeleteTab: (conversationId: string) => Promise<void>;
  /** Callback when a tab is renamed */
  onRenameTab: (conversationId: string, newTitle: string) => Promise<void>;
  /** Callback to exit campaign mode - handled by CopilotModeTabs now */
  onExitCampaignMode?: () => void;
  /** Whether a new tab is being created */
  isCreating?: boolean;
}

const ConversationTabs: React.FC<ConversationTabsProps> = ({
  isCampaignMode,
  campaignTitle,
}) => {
  // Only show in campaign mode
  if (!isCampaignMode) {
    return null;
  }

  return (
    <Box
      bg="orange.50"
      borderBottomWidth={1}
      borderColor="orange.100"
      px={3}
      py={1.5}
    >
      <Flex align="center" gap={2}>
        <Badge
          colorScheme="orange"
          variant="subtle"
          fontSize="10px"
          px={1.5}
          borderRadius="sm"
        >
          Helping with
        </Badge>
        <Text
          fontSize="sm"
          fontWeight="500"
          color="gray.700"
          noOfLines={1}
        >
          {campaignTitle || 'Campaign'}
        </Text>
      </Flex>
    </Box>
  );
};

export default ConversationTabs;
